import React, { useCallback, useEffect, useRef, useState } from "react";

import { getTeacherAdminMessages, sendTeacherAdminMessage } from "../services/teacherApi";

const errorText = (error, fallback) => error?.response?.data?.detail || fallback;

export default function TeacherAdminMessages({ onBack }) {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const data = await getTeacherAdminMessages();
      if (!mounted.current) return;
      const next = Array.isArray(data?.conversations) ? data.conversations : [];
      setConversations(next);
      setActiveId((current) => next.some((conv) => conv.id === current) ? current : (next[0]?.id || null));
      setError("");
    } catch (requestError) {
      if (mounted.current && !quiet) setError(errorText(requestError, "Impossible de charger la messagerie."));
    } finally {
      if (mounted.current && !quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const timer = window.setInterval(() => refresh({ quiet: true }), 8000);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, [refresh]);

  const active = conversations.find((conv) => conv.id === activeId) || null;

  const submit = async (event) => {
    event.preventDefault();
    const message = content.trim();
    if (!message || sending) return;
    setSending(true);
    setError("");
    try {
      await sendTeacherAdminMessage({ content: message, conversationId: active?.id });
      setContent("");
      await refresh({ quiet: true });
    } catch (requestError) {
      setError(errorText(requestError, "Le message n’a pas pu être envoyé."));
    } finally {
      if (mounted.current) setSending(false);
    }
  };

  return (
    <main className="ql-teacher-main ql-teacher-messages">
      <div className="ql-exam-toolbar">
        <button type="button" className="ql-exam-back" onClick={onBack}><i className="bi bi-arrow-left"></i> Dashboard</button>
        <div><span className="ql-teacher-eyebrow">Administration</span><h1>Messagerie</h1></div>
        <button type="button" className="ql-teacher-link" disabled={loading} onClick={() => refresh()}><i className="bi bi-arrow-clockwise"></i> Actualiser</button>
      </div>
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      {loading ? <div className="ql-teacher-state" role="status">Chargement de vos messages…</div> : (
        <section className="ql-teacher-message-layout">
          <aside aria-label="Conversations avec l’administration">
            {conversations.length === 0 ? <p>Aucune conversation. Votre premier message ouvrira une demande auprès de l’administration.</p> : conversations.map((conv) => (
              <button type="button" key={conv.id} className={conv.id === activeId ? "active" : ""} onClick={() => setActiveId(conv.id)}>
                <strong>{conv.admin?.name || "Administration"}</strong>
                {conv.unread_count > 0 && <span>{conv.unread_count}</span>}
              </button>
            ))}
          </aside>
          <div className="ql-teacher-message-thread">
            <div className="ql-teacher-message-list" aria-live="polite">
              {!active?.messages?.length && <p className="ql-teacher-message-empty">Écrivez à l’administration pour démarrer la conversation.</p>}
              {active?.messages?.map((message) => (
                <article key={message.id} className={message.sender === "generator" ? "sent" : "received"}>
                  <strong>{message.sender === "generator" ? "Vous" : message.author}</strong>
                  <p>{message.content}</p>
                  <time dateTime={message.created_at}>{new Date(message.created_at).toLocaleString("fr-FR")}</time>
                </article>
              ))}
            </div>
            <form onSubmit={submit}>
              <label htmlFor="teacher-admin-message">Votre message</label>
              <textarea id="teacher-admin-message" value={content} onChange={(event) => setContent(event.target.value)} maxLength={5000} rows={3} disabled={sending} />
              <button type="submit" className="btn btn-primary" disabled={sending || !content.trim()}>{sending ? "Envoi…" : "Envoyer"}</button>
            </form>
          </div>
        </section>
      )}
    </main>
  );
}
